// UPPDRAG-19 PR-A — conversion modal for anonymous users.
//
// Shown when an anonymous visitor tries to take an action that
// requires an account (Boka tid, Anmäl till match, …). Driven by
// AuthRequiredContext so any component can trigger it via
// `useAuthRequired().requireAuth({ intent, returnUrl })`.
//
// Two CTAs:
//   - "Skapa konto" (primary) → /auth?mode=signup&returnUrl=…
//   - "Logga in" (secondary) → /auth?mode=login&returnUrl=…
//
// Both routes use the same buildAuthUrl helper so the URL shape stays
// in sync with what Auth.tsx parses.

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuthRequired, type AuthRequiredIntent } from "@/contexts/AuthRequiredContext";
import { buildAuthUrl } from "@/lib/authRequiredHelpers";

/**
 * Resolve the i18n key suffix that drives the body copy for the given
 * intent. Falls back to "generic" if a future intent is added but the
 * locale files haven't caught up yet — better than rendering an empty
 * string.
 */
function bodyKeyFor(intent: AuthRequiredIntent): string {
  const known: AuthRequiredIntent[] = [
    "book_slot",
    "join_open_match",
    "register_team",
    "join_tournament",
    "generic",
  ];
  return known.includes(intent) ? intent : "generic";
}

export default function AuthRequiredModal() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { state, dismiss } = useAuthRequired();

  const handleNavigate = (mode: "login" | "signup") => {
    const url = buildAuthUrl(mode, state.returnUrl ?? undefined);
    dismiss();
    navigate(url);
  };

  return (
    <Dialog open={state.isOpen} onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("required.title", "Skapa konto eller logga in")}</DialogTitle>
          <DialogDescription>
            {t(
              `required.body.${bodyKeyFor(state.intent)}`,
              t("required.body.generic", "För att fortsätta behöver du ett konto."),
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col-reverse sm:flex-col-reverse gap-2 sm:gap-2">
          {/* Primary CTA — sign up. Most anonymous visitors are new. */}
          <Button
            onClick={() => handleNavigate("signup")}
            className="w-full"
          >
            {t("required.signup", "Skapa konto")}
          </Button>
          {/* Secondary — log in for returning users. */}
          <Button
            onClick={() => handleNavigate("login")}
            variant="outline"
            className="w-full"
          >
            {t("required.login", "Logga in")}
          </Button>
          {/* Tertiary — continue browsing without auth. */}
          <Button
            onClick={dismiss}
            variant="ghost"
            className="w-full"
          >
            {t("required.cancel", "Fortsätt utan konto")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
